import { useState } from "react";
import { Badge, Button, Dialog, Field, Icon, IconButton, Input, Select } from "../../ds/DS";
import type { Tone } from "../../ds/DS";
import { api } from "../../lib/api";
import type { VMilestone, VProject } from "../data";
import type { PageProps } from "../Portal";
import { relativeTime } from "../tone";
import {
  DialogFoot,
  PageHead,
  Progress,
  ScopeNote,
} from "./parts";
import { firstName, projectMilestones } from "./util";

const STATE_LABEL: Record<VMilestone["state"], string> = {
  done: "Signed off",
  active: "In build",
  next: "Planned",
};
const STATE_FROM_LABEL: Record<string, VMilestone["state"]> = {
  Planned: "next",
  "In build": "active",
  "Signed off": "done",
};

const awaiting = (m: VMilestone) => m.signoff === "pending" && m.state !== "done";

function MilestoneDialog({
  project,
  milestone,
  contact,
  onClose,
  onSaved,
  onDelete,
}: {
  project: VProject;
  milestone: VMilestone | null;
  contact: string;
  onClose: () => void;
  onSaved: () => void;
  onDelete: (m: VMilestone) => void;
}) {
  const [f, setF] = useState(() => ({
    name: milestone?.name ?? "",
    state: STATE_LABEL[milestone?.state ?? "next"],
    date: milestone?.dateAt ? new Date(milestone.dateAt).toISOString().slice(0, 10) : "",
    note: milestone?.note ?? "",
  }));
  const [busy, setBusy] = useState(false);
  const set = (k: keyof typeof f, v: string) => setF((s) => ({ ...s, [k]: v }));

  const save = async () => {
    if (!f.name.trim()) return;
    setBusy(true);
    const payload = {
      name: f.name.trim(),
      state: STATE_FROM_LABEL[f.state] ?? "next",
      dateAt: f.date ? Date.parse(f.date) : null,
      note: f.note,
    };
    try {
      if (milestone) {
        await api.patch(`/projects/${project.id}/milestones/${milestone.id}`, payload);
      } else {
        await api.post(`/projects/${project.id}/milestones`, payload);
      }
      onSaved();
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      title={milestone ? "Edit milestone" : "Add milestone"}
      description={`${project.name}.`}
      accent
      width={560}
      onClose={onClose}
      footer={
        <DialogFoot
          saveLabel="Save"
          busy={busy}
          invalid={!f.name.trim()}
          onCancel={onClose}
          onSave={() => void save()}
          deleteLabel={milestone ? "Delete milestone" : undefined}
          deleteWhat={milestone ? `“${milestone.name}”` : undefined}
          onDelete={milestone ? () => onDelete(milestone) : undefined}
        />
      }
    >
      <div className="stack-16">
        <Field label="Name" required>
          <Input value={f.name} onChange={(e) => set("name", e.target.value)} />
        </Field>
        <div className="grid-2">
          <Field label="State">
            <Select
              options={["Planned", "In build", "Signed off"]}
              value={f.state}
              onChange={(e) => set("state", e.target.value)}
            />
          </Field>
          <Field label="Date">
            <Input type="date" value={f.date} onChange={(e) => set("date", e.target.value)} />
          </Field>
        </div>
        <Field label="Note" hint={`${contact} sees this.`}>
          <Input multiline rows={3} value={f.note} onChange={(e) => set("note", e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}

function ProjectTimeline({
  project,
  milestones,
  isAdmin,
  contact,
  busyId,
  onAdd,
  onEdit,
  onAsk,
  onRemind,
  onSignOff,
}: {
  project: VProject;
  milestones: VMilestone[];
  isAdmin: boolean;
  contact: string;
  busyId: string | null;
  onAdd: () => void;
  onEdit: (m: VMilestone) => void;
  onAsk: (m: VMilestone) => void;
  onRemind: (m: VMilestone) => void;
  onSignOff: (m: VMilestone) => void;
}) {
  const done = milestones.filter((m) => m.state === "done").length;
  const meta = [project.code, project.status, project.due && `due ${project.due}`]
    .filter(Boolean)
    .join(" · ");
  return (
    <div className="ms-card">
      <div className="row-8 between ms-head">
        <div>
          <div className="ms-name">{project.name}</div>
          <div className="ms-meta mono">{meta}</div>
        </div>
        {isAdmin ? (
          <Button variant="secondary" size="sm" icon="plus" onClick={onAdd}>
            Add milestone
          </Button>
        ) : null}
      </div>
      <div className="ms-progress">
        <Progress
          pct={milestones.length ? Math.round((done / milestones.length) * 100) : 0}
          label={`${done} of ${milestones.length} signed off`}
        />
      </div>

      {milestones.length === 0 ? (
        <div className="ms-empty">No milestones yet.</div>
      ) : (
        milestones.map((m, i) => {
          const wait = awaiting(m);
          const tone: Tone = wait ? "warning" : m.state === "next" ? "neutral" : "teal";
          const busy = busyId === m.id;
          return (
            <div key={m.id} className={`ms-row is-${m.state}`}>
              <div className="ms-rail">
                <span className="ms-dot">
                  {m.state === "done" ? <Icon name="check" size={12} /> : null}
                </span>
                <span className={"ms-line" + (i === milestones.length - 1 ? " is-last" : "")} />
              </div>
              <div className="ms-body">
                <div className="ms-top">
                  <span className="ms-title">{m.name}</span>
                  <div className="ms-right">
                    <Badge tone={tone} dot={m.state === "active"}>
                      {wait ? "Awaiting sign-off" : STATE_LABEL[m.state]}
                    </Badge>
                    <span className="ms-date mono">{m.date}</span>
                    {isAdmin ? (
                      <IconButton
                        icon="pencil"
                        label="Edit milestone"
                        variant="ghost"
                        onClick={() => onEdit(m)}
                      />
                    ) : null}
                  </div>
                </div>
                {m.note ? <p className="ms-note">{m.note}</p> : null}
                {m.state === "done" && m.signedOffBy ? (
                  <p className="ms-signed mono">
                    Signed off by {m.signedOffBy}
                    {m.signedOffAt
                      ? ` on ${new Date(m.signedOffAt).toLocaleDateString("en-GB", {
                          day: "numeric",
                          month: "short",
                        })}`
                      : ""}
                  </p>
                ) : null}
                {wait ? (
                  <div className="ms-wait">
                    <span className="ms-wait-line">
                      <Icon name="hourglass" size={15} />
                      {isAdmin
                        ? `Waiting on ${contact} to sign this off.${
                            m.signoffRequestedAt
                              ? ` Asked ${relativeTime(m.signoffRequestedAt)}.`
                              : ""
                          }`
                        : "Ready for your sign-off."}
                    </span>
                    <div className="row-8">
                      {isAdmin ? (
                        <>
                          <Button
                            size="sm"
                            variant="secondary"
                            icon="bell"
                            disabled={busy}
                            onClick={() => onRemind(m)}
                          >
                            Remind
                          </Button>
                          <Button
                            size="sm"
                            variant="secondary"
                            icon="check"
                            disabled={busy}
                            onClick={() => onSignOff(m)}
                          >
                            Mark signed off
                          </Button>
                        </>
                      ) : (
                        <Button size="sm" icon="check" disabled={busy} onClick={() => onSignOff(m)}>
                          Sign off
                        </Button>
                      )}
                    </div>
                  </div>
                ) : null}
                {isAdmin && m.state === "active" && !m.signoff ? (
                  <button className="ms-ask" disabled={busy} onClick={() => onAsk(m)}>
                    Ask {contact} to sign off →
                  </button>
                ) : null}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}

export function Milestones({
  isAdmin,
  client,
  projects,
  milestones,
  pid,
  setPid,
  reload,
  toast,
  projectName,
}: PageProps) {
  const scopedProjects = pid === "all" ? projects : projects.filter((p) => p.id === pid);
  const contact = isAdmin ? (client?.contact ? firstName(client.contact) : "the client") : "you";
  const [dialog, setDialog] = useState<{ project: VProject; milestone: VMilestone | null } | null>(
    null,
  );
  const [busyId, setBusyId] = useState<string | null>(null);

  const run = async (m: VMilestone, path: string, done: (r: { next?: string | null }) => string) => {
    setBusyId(m.id);
    try {
      const r = await api.post<{ next?: string | null }>(`/milestones/${m.id}/${path}`);
      reload();
      toast(done(r ?? {}));
    } catch (e) {
      toast(e instanceof Error ? e.message : "That didn't work. Try again.");
    } finally {
      setBusyId(null);
    }
  };

  const del = async (project: VProject, m: VMilestone) => {
    await api.del(`/projects/${project.id}/milestones/${m.id}`);
    setDialog(null);
    reload();
    toast("Milestone removed.");
  };

  return (
    <div className="stack-24">
      <PageHead
        eyebrow="MILESTONES"
        title="Milestones"
        sub="Where each project is heading, and what's been signed off."
      />
      <ScopeNote pid={pid} setPid={setPid} projectName={projectName} />
      {scopedProjects.length === 0 ? (
        <div className="list-card">
          <div className="list-empty">No projects yet.</div>
        </div>
      ) : (
        scopedProjects.map((p) => (
          <ProjectTimeline
            key={p.id}
            project={p}
            milestones={projectMilestones(milestones, p.id)}
            isAdmin={isAdmin}
            contact={contact}
            busyId={busyId}
            onAdd={() => setDialog({ project: p, milestone: null })}
            onEdit={(m) => setDialog({ project: p, milestone: m })}
            onAsk={(m) =>
              void run(m, "request-signoff", () => `Sent to ${contact} for sign-off.`)
            }
            onRemind={(m) => void run(m, "remind", () => `Reminder sent to ${contact}.`)}
            onSignOff={(m) =>
              void run(m, "signoff", (r) =>
                r.next ? `Signed off. We'll move on to ${r.next}.` : "Signed off.",
              )
            }
          />
        ))
      )}

      {dialog ? (
        <MilestoneDialog
          project={dialog.project}
          milestone={dialog.milestone}
          contact={client?.contact ? firstName(client.contact) : "The client"}
          onClose={() => setDialog(null)}
          onDelete={(m) => void del(dialog.project, m)}
          onSaved={() => {
            reload();
            toast(dialog.milestone ? "Milestone updated." : "Milestone added.");
          }}
        />
      ) : null}
    </div>
  );
}
